import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, Eye, ArrowRight, Tag, Newspaper } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const ArticleCard = ({ article, featured = false }) => (
  <Link
    to={`/blog/${article.slug}`}
    className={`group block bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 ${featured ? 'md:col-span-2 md:grid md:grid-cols-2' : ''}`}
    data-testid={`article-card-${article.slug}`}
  >
    <div className={`relative overflow-hidden ${featured ? 'h-64 md:h-full' : 'h-48'}`}>
      {article.cover_image ? (
        <img
          src={article.cover_image}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center">
          <Newspaper className="w-16 h-16 text-white/30" />
        </div>
      )}
      <div className="absolute top-3 left-3">
        <span className="bg-[#0D9488] text-white text-xs font-semibold px-2.5 py-1 rounded-md">
          {article.category}
        </span>
      </div>
    </div>
    <div className="p-5">
      <div className="flex items-center gap-3 text-xs text-gray-400 mb-2">
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {formatDate(article.created_at)}
        </span>
        {article.views > 0 && (
          <span className="flex items-center gap-1">
            <Eye className="w-3 h-3" />
            {article.views}
          </span>
        )}
      </div>
      <h3 className={`font-bold text-[#1F2937] mb-2 group-hover:text-[#0D9488] transition-colors ${featured ? 'text-xl' : 'text-base'}`}>
        {article.title}
      </h3>
      <p className="text-sm text-gray-500 line-clamp-2 mb-3">
        {article.excerpt}
      </p>
      {article.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {article.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">
              {tag}
            </span>
          ))}
        </div>
      )}
      <span className="inline-flex items-center text-sm text-[#0D9488] font-medium group-hover:gap-2 transition-all">
        Lire la suite <ArrowRight className="w-3.5 h-3.5 ml-1" />
      </span>
    </div>
  </Link>
);

export const BlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('Tous');
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const LIMIT = 9;

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [activeCategory, search, page]);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/blog/categories`);
      setCategories(data);
    } catch {}
  };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, skip: page * LIMIT };
      if (activeCategory !== 'Tous') params.category = activeCategory;
      if (search) params.search = search;
      const { data } = await axios.get(`${API}/blog/articles`, { params });
      setArticles(data.articles);
      setTotal(data.total);
    } catch {} finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const featuredArticle = page === 0 && !search ? articles[0] : null;
  const gridArticles = featuredArticle ? articles.slice(1) : articles;

  return (
    <div className="bg-[#F8FAFC] min-h-[60vh]" data-testid="blog-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0F766E] to-[#0D9488] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 bg-white/10 text-white/90 text-xs font-semibold px-3 py-1 rounded-full mb-4">
            <Newspaper className="w-3 h-3" />
            BLOG & ACTUALITES
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="blog-title">
            Nos actualités
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Retrouvez nos derniers articles, conseils et actualités sur le monde de l'entreprise en Polynésie : RH, formation, recrutement et gestion de la paie.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8" data-testid="blog-filters">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Rechercher un article..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="pl-10"
              data-testid="blog-search"
            />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="blog-categories">
            <button
              onClick={() => { setActiveCategory('Tous'); setPage(0); }}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeCategory === 'Tous'
                  ? 'bg-[#0D9488] text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-[#0D9488] hover:text-[#0D9488]'
              }`}
              data-testid="category-tous"
            >
              Tous
            </button>
            {categories.map((cat) => (
              <button
                key={cat.name}
                onClick={() => { setActiveCategory(cat.name); setPage(0); }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === cat.name
                    ? 'bg-[#0D9488] text-white'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-[#0D9488] hover:text-[#0D9488]'
                }`}
                data-testid={`category-${cat.name}`}
              >
                {cat.name} ({cat.count})
              </button>
            ))}
          </div>
        </div>

        {/* Articles Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border animate-pulse">
                <div className="h-48 bg-gray-200 rounded-t-xl" />
                <div className="p-5 space-y-3">
                  <div className="h-3 bg-gray-200 rounded w-1/3" />
                  <div className="h-5 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20">
            <Newspaper className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-500">Aucun article trouve</h3>
            <p className="text-gray-400 mt-1">
              {search ? `Aucun résultat pour "${search}"` : 'Les articles arrivent bientôt !'}
            </p>
          </div>
        ) : (
          <>
            {/* Featured + Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
              {featuredArticle && <ArticleCard article={featuredArticle} featured />}
              {gridArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-10" data-testid="blog-pagination">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                      page === i
                        ? 'bg-[#0D9488] text-white'
                        : 'bg-white text-gray-600 border border-gray-200 hover:border-[#0D9488]'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
